import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function MedicalFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facilityType, setFacilityType] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  const facilityTypes = [
    { id: "all", name: "Todos" },
    { id: "hospital", name: "Hospitais" },
    { id: "clinic", name: "Clínicas" },
    { id: "lab", name: "Laboratórios" },
    { id: "pharmacy", name: "Farmácias" },
    { id: "dentist", name: "Dentistas" },
    { id: "vet", name: "Veterinários" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/medical?page=${pageNum}&limit=12`;
      if (facilityType !== "all") url += `&facilityType=${facilityType}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching medical:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [facilityType]);

  const handleEditPost = (post) => {
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) =>
      prev.map((p) => (p._id === updatedPost._id ? updatedPost : p))
    );
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
  };

  if (loading && posts.length === 0) {
    return <div className="medical_feed_loading"><div className="medical_loader"></div><span>Carregando estabelecimentos...</span></div>;
  }

  return (
    <div className="medical_feed">
      {facilityTypes.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {facilityTypes.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${facilityType === filter.id ? "active" : ""}`}
                onClick={() => setFacilityType(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="medical_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M19 3H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-4v-4H6v-4h4V6h4v4h4v4z"/>
          </svg>
          <h3>Nenhum estabelecimento encontrado</h3>
          <p>Cadastre o primeiro!</p>
        </div>
      ) : (
        <div className="medical_list">
          {posts.map((post) => (
            <div key={post._id} className="medical_card" onClick={() => setSelectedFacility(post)}>
              <div className="medical_card_image">
                <img src={post.medicalData?.image || post.images?.[0]?.url || post.images?.[0] || "/images/default-medical.png"} alt="" />
                <span className="medical_type_badge">
                  {facilityTypes.find(f => f.id === post.medicalData?.facilityType)?.name || "Saúde"}
                </span>
              </div>
              <div className="medical_card_content">
                <div className="medical_card_header">
                  <h3>{post.medicalData?.facilityName || post.text}</h3>
                  {post.medicalData?.emergency24h && (
                    <span className="medical_emergency">24h</span>
                  )}
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                    post={post}
                    onEdit={handleEditPost}
                    onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
                <div className="medical_specialties">
                  {post.medicalData?.specialties?.slice(0, 3).map((spec, i) => (
                    <span key={i}>{spec}</span>
                  ))}
                  {post.medicalData?.specialties?.length > 3 && (
                    <span>+{post.medicalData.specialties.length - 3}</span>
                  )}
                </div>
                <div className="medical_info">
                  <span className="medical_address">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    </svg>
                    {post.medicalData?.address || "Endereço não informado"}
                  </span>
                  {post.medicalData?.phone && (
                    <span className="medical_phone">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                      </svg>
                      {post.medicalData.phone}
                    </span>
                  )}
                </div>
                {post.medicalData?.insurances?.length > 0 && (
                  <div className="medical_insurances">
                    <span className="medical_insurances_label">Convênios:</span>
                    <span>{post.medicalData.insurances.slice(0, 2).join(", ")}</span>
                    {post.medicalData.insurances.length > 2 && <span> +{post.medicalData.insurances.length - 2}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="medical_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedFacility && (
        <div className="medical_modal_overlay" onClick={() => setSelectedFacility(null)}>
          <div className="medical_modal" onClick={(e) => e.stopPropagation()}>
            <button className="medical_modal_close" onClick={() => setSelectedFacility(null)}>✕</button>

            <div className="medical_modal_header">
              <img src={selectedFacility.medicalData?.image || selectedFacility.images?.[0]} alt="" />
            </div>

            <div className="medical_modal_body">
              <div className="medical_modal_title">
                <span className="medical_modal_type">
                  {facilityTypes.find(f => f.id === selectedFacility.medicalData?.facilityType)?.name}
                </span>
                <h2>{selectedFacility.medicalData?.facilityName}</h2>
                {selectedFacility.medicalData?.emergency24h && (
                  <span className="medical_modal_emergency">Emergência 24h</span>
                )}
              </div>

              {selectedFacility.medicalData?.description && (
                <p className="medical_modal_desc">{selectedFacility.medicalData.description}</p>
              )}

              <div className="medical_modal_details">
                <div className="medical_detail_item">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                  </svg>
                  <span>{selectedFacility.medicalData?.address}</span>
                </div>
                {selectedFacility.medicalData?.phone && (
                  <div className="medical_detail_item">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                    <span>{selectedFacility.medicalData.phone}</span>
                  </div>
                )}
                {selectedFacility.medicalData?.openingHours && (
                  <div className="medical_detail_item">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                    </svg>
                    <span>{selectedFacility.medicalData.openingHours}</span>
                  </div>
                )}
              </div>

              {selectedFacility.medicalData?.specialties?.length > 0 && (
                <div className="medical_modal_section">
                  <h4>Especialidades</h4>
                  <div className="medical_specialties_list">
                    {selectedFacility.medicalData.specialties.map((spec, i) => (
                      <span key={i}>{spec}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedFacility.medicalData?.insurances?.length > 0 && (
                <div className="medical_modal_section">
                  <h4>Convênios Aceitos</h4>
                  <div className="medical_insurances_list">
                    {selectedFacility.medicalData.insurances.map((ins, i) => (
                      <span key={i}>{ins}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedFacility.medicalData?.doctors?.length > 0 && (
                <div className="medical_modal_section">
                  <h4>Corpo Clínico</h4>
                  <div className="medical_doctors_list">
                    {selectedFacility.medicalData.doctors.map((doc, i) => (
                      <div key={i} className="medical_doctor_item">
                        <span className="medical_doctor_name">{doc.name}</span>
                        <span className="medical_doctor_specialty">{doc.specialty}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Link to={`/profile/${selectedFacility.user.username}`} className="medical_owner">
                <img src={selectedFacility.user.picture} alt="" />
                <div>
                  <span className="medical_owner_label">Responsável</span>
                  <span className="medical_owner_name">{selectedFacility.user.first_name} {selectedFacility.user.last_name}</span>
                </div>
              </Link>

              <div className="medical_modal_actions">
                {selectedFacility.medicalData?.phone && (
                  <a href={`tel:${selectedFacility.medicalData.phone}`} className="medical_call_btn">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                    Ligar
                  </a>
                )}
                {selectedFacility.medicalData?.whatsapp && (
                  <a href={`https://wa.me/${selectedFacility.medicalData.whatsapp}`} target="_blank" rel="noreferrer" className="medical_whatsapp_btn">
                    WhatsApp
                  </a>
                )}
                <button className="medical_schedule_btn">Agendar consulta</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
